import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, EmptyState, StatusChip, DataTableCard } from '../components/ui';
import { brand } from '../theme';

const MATRIX_COLS = [
  { key: 'feature', label: 'Feature', width: '28%' },
  { key: 'all', label: 'All', width: '12%' },
  { key: 'view', label: 'View', width: '12%' },
  { key: 'create', label: 'Create', width: '12%' },
  { key: 'manage', label: 'Manage', width: '18%' },
  { key: 'download', label: 'Download', width: '18%' }
];

function buildPermissionGroups(permissionsList) {
  const groups = {};
  const labelFor = (base, fallback) =>
    permissionsList.find((p) => p.key === base)?.label || fallback;

  permissionsList.forEach(({ key, label }) => {
    if (key.endsWith('_manage')) {
      const base = key.slice(0, -'_manage'.length);
      if (!groups[base]) {
        groups[base] = { key: base, label, viewKey: base, manageKey: key, createKey: null, exportKey: null };
      } else {
        groups[base].manageKey = key;
      }
    } else if (key.endsWith('_create')) {
      const base = key.slice(0, -'_create'.length);
      if (!groups[base]) {
        groups[base] = { key: base, label, viewKey: base, manageKey: null, createKey: key, exportKey: null };
      } else {
        groups[base].createKey = key;
      }
    } else if (key.endsWith('_export')) {
      const base = key.slice(0, -'_export'.length);
      if (!groups[base]) {
        groups[base] = {
          key: base,
          label: labelFor(base, label),
          viewKey: base,
          manageKey: null,
          createKey: null,
          exportKey: key
        };
      } else {
        groups[base].exportKey = key;
      }
    } else if (!groups[key]) {
      groups[key] = { key, label, viewKey: key, manageKey: null, createKey: null, exportKey: null };
    } else if (groups[key].viewKey === key) {
      groups[key].label = label;
    }
  });

  return Object.values(groups);
}

function PermissionsMatrix({ groups, hasPermission, onToggle, onToggleAll }) {
  return (
    <TableContainer
      sx={{
        border: `1px solid ${brand.border}`,
        borderRadius: 2,
        maxHeight: 420,
        bgcolor: '#fff'
      }}
    >
      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 640 }}>
        <TableHead>
          <TableRow>
            {MATRIX_COLS.map((col) => (
              <TableCell
                key={col.key}
                align={col.key === 'feature' ? 'left' : 'center'}
                sx={{
                  width: col.width,
                  bgcolor: '#f8fafc',
                  color: '#64748b',
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  borderBottom: `1px solid ${brand.border}`,
                  py: 1.25
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const viewOn = hasPermission(group.viewKey);
            const createOn = group.createKey ? hasPermission(group.createKey) : true;
            const manageOn = group.manageKey ? hasPermission(group.manageKey) : true;
            const exportOn = group.exportKey ? hasPermission(group.exportKey) : true;
            const allOn = viewOn && createOn && manageOn && exportOn;

            return (
              <TableRow key={group.key} hover>
                <TableCell sx={{ fontWeight: 600, color: brand.secondary, fontSize: 13.5 }}>
                  {group.label}
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    size="small"
                    checked={allOn}
                    onChange={() => onToggleAll(group)}
                    sx={{ color: brand.primary, '&.Mui-checked': { color: brand.primary } }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    size="small"
                    checked={hasPermission(group.viewKey)}
                    onChange={() => onToggle(group.viewKey)}
                    sx={{ color: brand.primary, '&.Mui-checked': { color: brand.primary } }}
                  />
                </TableCell>
                <TableCell align="center">
                  {group.createKey ? (
                    <Checkbox
                      size="small"
                      checked={hasPermission(group.createKey)}
                      onChange={() => onToggle(group.createKey)}
                      title="Create without view access"
                      sx={{ color: brand.primary, '&.Mui-checked': { color: brand.primary } }}
                    />
                  ) : (
                    <Typography color="text.disabled" component="span">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {group.manageKey ? (
                    <Checkbox
                      size="small"
                      checked={hasPermission(group.manageKey)}
                      onChange={() => onToggle(group.manageKey)}
                      sx={{ color: brand.primary, '&.Mui-checked': { color: brand.primary } }}
                    />
                  ) : (
                    <Typography color="text.disabled" component="span">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {group.exportKey ? (
                    <Checkbox
                      size="small"
                      checked={hasPermission(group.exportKey)}
                      onChange={() => onToggle(group.exportKey)}
                      title="Download Excel (CSV)"
                      sx={{ color: brand.primary, '&.Mui-checked': { color: brand.primary } }}
                    />
                  ) : (
                    <Typography color="text.disabled" component="span">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function toggleAllForGroup(group, current, setPermissions) {
  const viewOn = current.includes(group.viewKey);
  const createOn = group.createKey ? current.includes(group.createKey) : true;
  const manageOn = group.manageKey ? current.includes(group.manageKey) : true;
  const exportOn = group.exportKey ? current.includes(group.exportKey) : true;
  const allOn = viewOn && createOn && manageOn && exportOn;

  if (allOn) {
    setPermissions(
      current.filter(
        (p) =>
          p !== group.viewKey &&
          p !== group.createKey &&
          p !== group.manageKey &&
          p !== group.exportKey
      )
    );
    return;
  }

  const next = [...current];
  if (!viewOn) next.push(group.viewKey);
  if (group.createKey && !createOn) next.push(group.createKey);
  if (group.manageKey && !manageOn) next.push(group.manageKey);
  if (group.exportKey && !exportOn) next.push(group.exportKey);
  setPermissions(Array.from(new Set(next)));
}

const RoleBuilder = () => {
  const { isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [newRole, setNewRole] = useState({ name: '', description: '', permissions: [] });
  const [editingRole, setEditingRole] = useState(null);
  const [editRoleForm, setEditRoleForm] = useState({ description: '', permissions: [], isActive: true });

  const { data: permsData } = useQuery('admin-permissions', adminAPI.getPermissions, { enabled: isSuperAdmin });
  const permissionsList = permsData?.data?.data || [];

  const { data: rolesData, isLoading, error } = useQuery('admin-roles', adminAPI.getRoles, { enabled: isSuperAdmin });
  const roles = rolesData?.data?.data || [];
  const customRoles = roles.filter((r) => !r.isSystem);

  const permissionGroups = useMemo(() => buildPermissionGroups(permissionsList), [permissionsList]);

  const createRoleMutation = useMutation((payload) => adminAPI.createRole(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-roles');
      setNewRole({ name: '', description: '', permissions: [] });
      showToast({ message: 'Role created.', severity: 'success' });
    },
    onError: (err) =>
      showToast({
        message: err.response?.data?.message || 'Failed to create role',
        severity: 'error'
      })
  });

  const updateRoleMutation = useMutation(({ id, payload }) => adminAPI.updateRole(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-roles');
      setEditingRole(null);
      showToast({ message: 'Role updated.', severity: 'success' });
    },
    onError: (err) =>
      showToast({
        message: err.response?.data?.message || 'Failed to update role',
        severity: 'error'
      })
  });

  const deleteRoleMutation = useMutation((id) => adminAPI.deleteRole(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-roles');
      showToast({ message: 'Role deleted.', severity: 'success' });
    },
    onError: (err) =>
      showToast({
        message: err.response?.data?.message || 'Failed to delete role',
        severity: 'error'
      })
  });

  const toggleNewPermission = (permKey) => {
    setNewRole((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter((p) => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const toggleEditPermission = (permKey) => {
    setEditRoleForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter((p) => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  if (!isSuperAdmin) {
    return (
      <Box>
        <PageHeader title="Role Builder" />
        <Card>
          <CardContent>
            <Typography color="text.secondary">Only super admins can manage roles.</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Role Builder"
        subtitle="Create, edit and delete custom roles with detailed module permissions."
      />

      <Stack spacing={2.5}>
        <Card>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Custom roles
            </Typography>
            {isLoading ? (
              <Typography color="text.secondary">Loading roles…</Typography>
            ) : error ? (
              <Typography color="error">Failed to load roles.</Typography>
            ) : customRoles.length === 0 ? (
              <EmptyState
                title="No custom roles yet"
                description="Create a role below to assign module permissions."
                sx={{ border: 'none', py: 2 }}
              />
            ) : (
              <DataTableCard sx={{ boxShadow: 'none', border: `1px solid ${brand.border}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customRoles.map((role) => (
                      <TableRow key={role.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{role.name}</TableCell>
                        <TableCell>{role.description || '—'}</TableCell>
                        <TableCell>
                          {Array.isArray(role.permissions) ? role.permissions.length : 0}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            status={role.isActive ? 'active' : 'inactive'}
                            label={role.isActive ? 'Active' : 'Inactive'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setEditingRole(role);
                                setEditRoleForm({
                                  description: role.description || '',
                                  permissions: Array.isArray(role.permissions)
                                    ? [...role.permissions]
                                    : [],
                                  isActive: !!role.isActive
                                });
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={deleteRoleMutation.isLoading}
                              onClick={() => {
                                if (window.confirm(`Delete role "${role.name}"?`)) {
                                  deleteRoleMutation.mutate(role.id);
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataTableCard>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Create custom role
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Name the role, then tick the module permissions below.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr auto' },
                gap: 2,
                alignItems: 'start',
                mb: 2.5
              }}
            >
              <TextField
                label="Role name"
                placeholder="e.g. operations_manager"
                value={newRole.name}
                onChange={(e) => setNewRole((p) => ({ ...p, name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Description"
                placeholder="Short description for this role"
                value={newRole.description}
                onChange={(e) => setNewRole((p) => ({ ...p, description: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={1}
              />
              <Button
                variant="contained"
                disabled={createRoleMutation.isLoading || !newRole.name.trim()}
                onClick={() => createRoleMutation.mutate(newRole)}
                sx={{ height: 40, px: 2.5, whiteSpace: 'nowrap' }}
              >
                {createRoleMutation.isLoading ? 'Creating…' : 'Create role'}
              </Button>
            </Box>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
              Module permissions
              {newRole.permissions.length > 0 ? (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({newRole.permissions.length} selected)
                </Typography>
              ) : null}
            </Typography>

            <PermissionsMatrix
              groups={permissionGroups}
              hasPermission={(key) => newRole.permissions.includes(key)}
              onToggle={toggleNewPermission}
              onToggleAll={(group) =>
                toggleAllForGroup(group, newRole.permissions, (next) =>
                  setNewRole((prev) => ({ ...prev, permissions: next }))
                )
              }
            />
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={Boolean(editingRole)}
        onClose={() => setEditingRole(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit role: {editingRole?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Description"
              value={editRoleForm.description}
              onChange={(e) => setEditRoleForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControl size="small" sx={{ maxWidth: 220 }}>
              <InputLabel id="role-active-label">Status</InputLabel>
              <Select
                labelId="role-active-label"
                label="Status"
                value={String(editRoleForm.isActive)}
                onChange={(e) =>
                  setEditRoleForm((p) => ({ ...p, isActive: e.target.value === 'true' }))
                }
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Module permissions
              </Typography>
              <PermissionsMatrix
                groups={permissionGroups}
                hasPermission={(key) => editRoleForm.permissions.includes(key)}
                onToggle={toggleEditPermission}
                onToggleAll={(group) =>
                  toggleAllForGroup(group, editRoleForm.permissions, (next) =>
                    setEditRoleForm((prev) => ({ ...prev, permissions: next }))
                  )
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setEditingRole(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={updateRoleMutation.isLoading}
            onClick={() =>
              updateRoleMutation.mutate({
                id: editingRole.id,
                payload: {
                  description: editRoleForm.description,
                  permissions: editRoleForm.permissions,
                  isActive: editRoleForm.isActive
                }
              })
            }
          >
            {updateRoleMutation.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleBuilder;
